import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Empty,
  Flex,
  Form,
  Select,
  Spin,
  Tabs,
  Typography,
  theme,
} from '@duxton-web/ui';
import {
  SearchUniversal,
} from '@duxton-web/icons';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
  COMMS_TRACE_PAGE_SIZE,
  searchCommsTrace,
} from 'omega-rtc/actions/debugging-tools';
import type { SearchCommsTraceParams } from 'omega-rtc/actions/debugging-tools';
import {
  getBrowserTimezone,
  getTimezoneAbbreviation,
} from 'omega-rtc/utils/time-utils';
import DebuggingToolsDrawer from './DebuggingToolsDrawer';
import CommsTraceUserTimeline from './CommsTraceUserTimeline';
import {
  normalizeUserIDs,
  toTraceRange,
  USER_ID_TOKEN_SEPARATORS,
  validateRange,
  validateUserIDs,
} from './commsTraceForm';
import { orderUserResults } from './commsTraceTimeline';
import type { CommsTraceJourney, CommsTraceUserResult } from './types';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const DebuggingToolsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [selectedJourney, setSelectedJourney] = useState<CommsTraceJourney | null>(null);
  const [activeUserId, setActiveUserId] = useState<string | undefined>(undefined);

  const users: Record<string, CommsTraceUserResult> = useSelector((state: any) =>
    _.get(state, 'debuggingTools.users', {}),
  );
  const page: number = useSelector((state: any) => _.get(state, 'debuggingTools.page', 1));
  const pageSize: number = useSelector((state: any) =>
    _.get(state, 'debuggingTools.pageSize', COMMS_TRACE_PAGE_SIZE),
  );
  const lastQuery: SearchCommsTraceParams | null = useSelector((state: any) =>
    _.get(state, 'debuggingTools.lastQuery', null),
  );
  const hasSearched: boolean = useSelector((state: any) =>
    _.get(state, 'debuggingTools.hasSearched', false),
  );
  const notConfigured: boolean = useSelector((state: any) =>
    _.get(state, 'debuggingTools.notConfigured', false),
  );
  const countries = useSelector((state: any) => _.get(state, 'countries.entities', []));
  const searchRequest = useSelector((state: any) =>
    _.get(state.requests, 'searchCommsTraceRequest', {}),
  );
  const isPending = Boolean(searchRequest.isPending);

  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const browserTimezoneCode = getTimezoneAbbreviation(countries, browserTimezone);

  const userGroups = useMemo(
    () => orderUserResults(users, lastQuery?.userIDs),
    [users, lastQuery],
  );
  const activeUserResult = activeUserId ? users[activeUserId] : undefined;

  // A new search clears the previous result set, so the drawer must not keep showing one of its rows.
  useEffect(() => {
    if (isPending) setSelectedJourney(null);
  }, [isPending]);

  useEffect(() => {
    if (hasSearched && !userGroups.some(group => group.result.journeys.length)) {
      setSelectedJourney(null);
    }
  }, [hasSearched, userGroups]);

  useEffect(() => {
    if (!userGroups.length) {
      if (!isPending) setActiveUserId(undefined);
      return;
    }
    if (!activeUserId || !userGroups.some(group => group.userId === activeUserId)) {
      setActiveUserId(userGroups[0].userId);
    }
  }, [userGroups, activeUserId, isPending]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handler = (event: ErrorEvent) => {
        if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
          event.stopImmediatePropagation();
        }
      };
      window.addEventListener('error', handler);
      return () => window.removeEventListener('error', handler);
    }
  }, []);

  const onSearch = () => {
    setSelectedJourney(null);
    form
      .validateFields()
      .then((values: any) => {
        const { userIDs, dateRange } = values;
        const { startTime, endTime } = toTraceRange(dateRange, browserTimezone);
        dispatch(
          searchCommsTrace({
            userIDs: normalizeUserIDs(userIDs),
            startTime,
            endTime,
            timezone: browserTimezone,
            page: 1,
            pageSize: COMMS_TRACE_PAGE_SIZE,
          }),
        );
      })
      .catch((error: any) => {
        if (!error?.errorFields) console.error('Search failed:', error);
      });
  };

  const onPageChange = (nextPage: number) => {
    if (!lastQuery) return;
    setSelectedJourney(null);
    dispatch(
      searchCommsTrace({
        ...lastQuery,
        page: nextPage,
        pageSize,
      }),
    );
  };

  const onUserChange = (nextUserId: string) => {
    setActiveUserId(nextUserId);
    if (nextUserId !== activeUserId && page !== 1) onPageChange(1);
  };

  const tabItems = useMemo(
    () =>
      userGroups.map(group => ({
        key: group.userId,
        label: group.userId,
        children: (
          <CommsTraceUserTimeline
            journeys={group.result.journeys}
            total={group.result.total}
            truncated={group.result.truncated}
            page={page}
            pageSize={pageSize}
            timezone={browserTimezone}
            timezoneCode={browserTimezoneCode}
            onSelectJourney={setSelectedJourney}
            onPageChange={onPageChange}
          />
        ),
      })),
    [userGroups, page, pageSize, browserTimezone, browserTimezoneCode],
  );

  const renderResults = () => {
    if (!hasSearched && !isPending) {
      return <Empty description="No search has run yet." />;
    }

    if (hasSearched && notConfigured && !isPending) {
      return <Empty description="Comms trace is not configured for this environment yet" />;
    }

    if (
      hasSearched
      && !userGroups.some(group => group.result.total > 0 || group.result.journeys.length > 0)
      && !isPending
    ) {
      return <Empty description="No journeys found for these users in the selected range." />;
    }

    return (
      <div style={{ paddingTop: token.paddingSM }}>
        <Spin spinning={isPending}>
          <Tabs
            type="pill"
            tabPosition="top"
            activeKey={activeUserId}
            onChange={onUserChange}
            items={tabItems}
            style={{ marginBottom: token.marginMD }}
          />
        </Spin>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: '100%',
        padding: token.paddingLG,
        background: token.colorBgContainer,
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{ marginBottom: token.marginLG }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Where&apos;s My Comms
        </Title>
        {hasSearched && !notConfigured && (
          <Flex vertical align="flex-end">
            <Typography.Metadata type="secondary">
              {activeUserResult?.total || 0} journeys
            </Typography.Metadata>
          </Flex>
        )}
      </Flex>
      <div style={{ marginBottom: '24px' }}>
        <Form form={form} layout="inline" colon={false}>
          <Flex wrap="wrap" align="flex-start" gap={12}>
            <Form.Item
              name="userIDs"
              rules={[
                {
                  validator: (_rule: unknown, value: string[]) => {
                    const message = validateUserIDs(value);
                    return message ? Promise.reject(new Error(message)) : Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="tags"
                tokenSeparators={USER_ID_TOKEN_SEPARATORS}
                placeholder="User IDs (paste up to 20)"
                open={false}
                style={{ width: '280px', minHeight: '36px' }}
              />
            </Form.Item>
            <Form.Item
              name="dateRange"
              rules={[
                {
                  validator: (_rule: unknown, value: any) => {
                    const message = validateRange(value, browserTimezone);
                    return message ? Promise.reject(new Error(message)) : Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <RangePicker
                showTime
                needConfirm={false}
                format="YYYY-MM-DD HH:mm"
                placeholder={['Start date', 'End date']}
                onCalendarChange={dates => form.setFieldValue('dateRange', dates)}
                style={{ height: '36px' }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={onSearch}
                loading={isPending}
                disabled={isPending}
                icon={<SearchUniversal />}
                style={{ height: '36px' }}
              >
                Search
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </div>
      {renderResults()}
      <DebuggingToolsDrawer
        journey={selectedJourney}
        timezone={browserTimezone}
        timezoneCode={browserTimezoneCode}
        open={!!selectedJourney}
        onClose={() => setSelectedJourney(null)}
      />
    </div>
  );
};

export default DebuggingToolsPage;
